import React from 'react';
import WiedzaArticleLayout from '../components/WiedzaArticleLayout';

/**
 * Article 2 — "Why HussarAi Does Not Let AI Make Immigration Decisions"
 * PL:  Dlaczego HussarAi nie pozwala AI podejmować decyzji imigracyjnych
 * Route (PL primary): /wiedza/dlaczego-hussarai-nie-pozwala-ai-decydowac
 * Route (EN alias):   /knowledge/why-hussarai-does-not-let-ai-make-immigration-decisions
 * Published: 2026-08-17
 */
const WiedzaHussaraiNieDecyduje = () => (
  <WiedzaArticleLayout
    slugId="why-hussarai-does-not-let-ai-make-immigration-decisions"
    canonicalUrl="https://hussarai.org/wiedza/dlaczego-hussarai-nie-pozwala-ai-decydowac"
    publishedDate="2026-08-17"
    meta={{
      titlePl: 'Dlaczego HussarAi nie pozwala AI podejmować decyzji imigracyjnych',
      titleEn: 'Why HussarAi Does Not Let AI Make Immigration Decisions',
      descriptionPl:
        'HussarAi celowo zatrzymuje się przed warstwą decyzyjną. ' +
        'Cztery warstwy: fakty źródłowe, sygnały weryfikacyjne, ' +
        'weryfikacja przez człowieka, decyzja właściwego urzędu.',
      descriptionEn:
        'HussarAi intentionally stops before the decision layer. Four ' +
        'layers: source facts, review signals, human review, external ' +
        'authority decision.',
    }}
    content={{
      eyebrowPl: 'Wiedza · Doktryna produktu',
      eyebrowEn: 'Knowledge · Product doctrine',
      ledePl:
        'Nie każde ograniczenie AI jest wadą techniczną. W HussarAi ' +
        'granica między AI a decyzją imigracyjną jest częścią projektu — ' +
        'zamierzona, jawna i twarda.',
      ledeEn:
        'Not every AI limitation is a technical shortcoming. In HussarAi, ' +
        'the boundary between AI and immigration decision-making is a ' +
        'design choice — deliberate, explicit, and hard.',
      bodyPl: [
        { type: 'h2', text: 'Cztery warstwy' },
        { type: 'p', text:
          'Cały produkt zbudowany jest wokół czterowarstwowego modelu:' },
        { type: 'callout', text:
          'Warstwa 1 — Fakty źródłowe. Warstwa 2 — Analiza / sygnały weryfikacyjne. Warstwa 3 — Weryfikacja przez człowieka. Warstwa 4 — Decyzja właściwego urzędu.' },
        { type: 'p', text:
          'HussarAi działa wyłącznie w warstwach 1–3. Warstwa 4 nie jest naszym obszarem — należy do urzędów, sądów i uprawnionych podmiotów. Nie zbliżamy się do niej ani „prawie decyzją”, ani „rekomendacją”, ani „prawdopodobnym wynikiem”. To granica architektoniczna, nie kosmetyczna.' },
        { type: 'h2', text: 'Warstwa 1 — Fakty źródłowe' },
        { type: 'p', text:
          'AI wyodrębnia atomowe fakty ze wgranych dokumentów: daty, wskazówki typu dokumentu, zamaskowane numery dokumentów podróży, fragmenty imion. Każdy fakt jest powiązany z konkretną stroną i konkretnym cytatem. Nie zgadujemy — jeśli tekstu nie ma, oznaczamy dokument jako wymagający manualnej weryfikacji.' },
        { type: 'h2', text: 'Warstwa 2 — Sygnały, nie werdykty' },
        { type: 'p', text:
          'Nad wyodrębnionymi faktami działa determistyczna analiza spójności: powtarzające się dokumenty, daty wcześniejsze niż dzień dzisiejszy, niemożliwe chronologie, wiele wariantów zapisu nazwiska. Wynik to lista sygnałów do przeglądu, nigdy oskarżeń wobec ludzi. Poziom pilności („niski / średni / wysoki”) mówi o pilności przeglądu, nie o wyniku sprawy.' },
        { type: 'h2', text: 'Warstwa 3 — Weryfikacja przez człowieka' },
        { type: 'p', text:
          'Każdy sygnał trafia do człowieka: właściciela akt, zaufanego pomocnika lub przydzielonego recenzenta. Człowiek ma cztery czyste stany: przyjmuję, odrzucam, oznaczam jako wymagające manualnej weryfikacji, wyjaśniam. Każda decyzja człowieka jest zapisana w łańcuchu audytowym HMAC — nie można jej „cofnąć” bez śladu.' },
        { type: 'h2', text: 'Warstwa 4 — Decyzje należą do urzędów' },
        { type: 'p', text:
          'Status imigracyjny w Polsce ustala właściwy urząd, wnioski składa się przez oficjalny system MOS. HussarAi nie jest MOS, nie jest urzędem i nie ma żadnego uprawnienia do rozstrzygnięć imigracyjnych. Nie „ocenia szans”, nie „rekomenduje decyzji”, nie „przewiduje wyniku”.' },
        { type: 'h2', text: 'Dlaczego ta granica' },
        { type: 'p', text:
          'Bo utrzymanie granicy jest jedynym sposobem, by AI faktycznie pomagało ludziom bez odbierania im podmiotowości. HussarAi nie sprawdza wartości człowieka — sprawdza spójność akt.' },
      ],
      bodyEn: [
        { type: 'h2', text: 'Four layers' },
        { type: 'p', text:
          'The whole product is built around a four-layer model:' },
        { type: 'callout', text:
          'Layer 1 — Source facts. Layer 2 — Analysis / review signals. Layer 3 — Human review. Layer 4 — Decision by the competent authority.' },
        { type: 'p', text:
          'HussarAi operates only in Layers 1–3. Layer 4 is not our area — it belongs to public authorities, courts, and authorised bodies. We do not approach it with "almost-decisions", "recommendations", or "likely outcomes". This is an architectural boundary, not a cosmetic one.' },
        { type: 'h2', text: 'Layer 1 — Source facts' },
        { type: 'p', text:
          'AI extracts atomic facts from uploaded documents: dates, document-type hints, masked travel-document numbers, name-line fragments. Each fact is linked to a specific page and a specific quote. We do not guess — if the text is not there, we mark the document as needing manual review.' },
        { type: 'h2', text: 'Layer 2 — Signals, not verdicts' },
        { type: 'p', text:
          'On top of extracted facts, a deterministic consistency analysis runs: duplicate documents, dates earlier than today, impossible chronologies, multiple name spellings. The result is a list of review signals, never accusations against people. An urgency level ("low / medium / high") speaks to review urgency, not case outcome.' },
        { type: 'h2', text: 'Layer 3 — Human review' },
        { type: 'p', text:
          'Every signal reaches a human: the record owner, a trusted helper, or an assigned reviewer. The human has four clean states: accept, dismiss, mark as needing manual review, explain. Every human action is recorded in the HMAC-anchored audit chain — it cannot be "undone" without a trace.' },
        { type: 'h2', text: 'Layer 4 — Decisions belong to authorities' },
        { type: 'p', text:
          'Immigration status in Poland is determined by the competent authority; applications are submitted through the official MOS system. HussarAi is not MOS, is not a government body, and holds no authority to make immigration decisions. It does not "assess chances", does not "recommend decisions", does not "predict outcomes".' },
        { type: 'h2', text: 'Why this boundary' },
        { type: 'p', text:
          'Because maintaining the boundary is the only way AI can actually help people without taking their agency away. HussarAi does not check the worth of a person — it checks the consistency of a record.' },
      ],
    }}
    internalLinks={[
      { to: '/wiedza', labelPl: 'Wiedza', labelEn: 'Knowledge' },
      { to: '/mos/precheck', labelPl: 'Weryfikacja przed MOS', labelEn: 'MOS PreCheck' },
      { to: '/bezpieczenstwo-i-zgodnosc', labelPl: 'Bezpieczeństwo i zgodność', labelEn: 'Security and governance' },
    ]}
  />
);

export default WiedzaHussaraiNieDecyduje;
